/**
 * Line sheet view tracking (KP-3741).
 *
 * A sequence email carries the buyer's wishlist link as the real marketplace
 * URL, with an opaque tracked-link id on it — nothing is wrapped in a redirect,
 * so nothing in the email looks machine-sent and no link subdomain has to
 * exist. Nothing counts the visit on the way in, so this page counts it on
 * arrival.
 *
 * Reporting from the rendered page rather than from a redirect is what makes
 * the number trustworthy: an inbox security scanner fetches URLs but never runs
 * the script, and scanner inflation is exactly why the sequence report cannot
 * treat an "open" as interest.
 */

/** Carries the tracked-link id. Mirrors linesheet.TrackingIDParam in plg-go-be. */
export const TRACKING_ID_PARAM = 'ks'

/** Path of the public ingest endpoint on plg-go-be. */
export const VIEW_ENDPOINT_PATH = '/api/v1/public/line-sheet-view'

/**
 * Decides whether this page load should report a line sheet visit.
 *
 * Returns the tracked-link id to report, or null to stay quiet. Kept pure so
 * the decision is testable without a browser.
 *
 * @param {object} query        route query parameters
 * @param {boolean} inIframe    true when embedded in the PLG app
 */
export function viewToReport(query, inIframe) {
  if (!query) return null

  // The embedded copy is a brand previewing their OWN line sheet inside PLG,
  // not a buyer opening the link from an email. Counting it would let a brand
  // inflate its own engagement just by looking at its collection.
  if (inIframe) return null

  const id = query[TRACKING_ID_PARAM]
  return typeof id === 'string' && id.length > 0 ? id : null
}

/**
 * Reports the visit, and never lets a reporting problem reach the buyer.
 *
 * `sendBeacon` is deliberate: it is fire-and-forget, survives the page being
 * navigated away from immediately, and posts a simple request that needs no
 * CORS preflight against the PLG origin. Falls back to a keepalive fetch where
 * it is unavailable.
 *
 * A missing plgBaseUrl is a no-op rather than an error — the buyer is here to
 * look at a collection, and our bookkeeping is not their problem.
 */
export function reportLineSheetView(trackedLinkId, plgBaseUrl) {
  if (!trackedLinkId || !plgBaseUrl) return false

  const url = `${String(plgBaseUrl).replace(/\/+$/, '')}${VIEW_ENDPOINT_PATH}`
  const payload = JSON.stringify({ id: trackedLinkId })

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      return navigator.sendBeacon(url, payload)
    }
    if (typeof fetch === 'function') {
      fetch(url, {
        method: 'POST',
        body: payload,
        keepalive: true,
        mode: 'no-cors',
      }).catch(() => {})
      return true
    }
  } catch (e) {
    // Swallowed on purpose: see above.
  }
  return false
}
